/**
 * THE TWO SMALL FORMS A RECORDED CHANGE STILL NEEDS: how she already knows, which moves the visit
 * now, and whether a cancellation is charged. The waiting panel and the history card both draw
 * the first.
 */
import { useState } from 'react';
import {
  CHARGE_DECISIONS,
  CHARGE_DECISION_LABELS,
  SILENCE_REASONS,
  SILENCE_REASON_LABELS,
  type ChargeDecision,
  type SilenceReason,
} from '@fk/shared';
import { ApiError, decideCharge, silenceChange } from '../../api.ts';
import { Button, SelectField } from '../../components/Field.tsx';
import { ErrorNotice } from '../../components/Notice.tsx';

/** "I told her another way": the escape hatch that keeps a change from sitting pending for ever. */
export function SilenceForm({ changeId, onDone }: { changeId: string; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<SilenceReason | ''>('');
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!reason) {
      setProblem('Choose how they know.');
      return;
    }
    setSaving(true);
    setProblem(null);
    try {
      await silenceChange(changeId, reason);
      onDone();
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not recorded.');
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <div className="mt-3">
        <Button tone="quiet" onClick={() => setOpen(true)}>
          I told them another way
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-lg border border-navy/15 p-3">
      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not recorded" message={problem} />
        </div>
      ) : null}
      <SelectField
        id={`silence-${changeId}`}
        label="How they know"
        required
        value={reason}
        onChange={setReason}
        options={SILENCE_REASONS}
        toLabel={(value) => SILENCE_REASON_LABELS[value]}
        placeholder="Choose one"
      />
      <p className="mb-3 text-xs text-navy/65">
        Any message still waiting to go is withdrawn, and the visit moves now.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={submit} disabled={saving}>
          {saving ? 'Recording' : 'Record it and move the visit'}
        </Button>
        <Button tone="quiet" onClick={() => setOpen(false)} disabled={saving}>
          Never mind
        </Button>
      </div>
    </div>
  );
}

export function ChargeForm({ changeId, onDone }: { changeId: string; onDone: () => void }) {
  const [decision, setDecision] = useState<ChargeDecision | ''>('');
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!decision || decision === 'not_decided') {
      setProblem('Choose whether this cancellation is charged.');
      return;
    }
    setSaving(true);
    setProblem(null);
    try {
      await decideCharge(changeId, decision);
      onDone();
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not recorded.');
      setSaving(false);
    }
  }

  return (
    <div className="mt-3 rounded-lg border border-navy/15 p-3">
      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not recorded" message={problem} />
        </div>
      ) : null}
      <SelectField
        id={`charge-${changeId}`}
        label="Is this cancellation charged"
        value={decision}
        onChange={setDecision}
        options={CHARGE_DECISIONS.filter((value) => value !== 'not_decided')}
        toLabel={(value) => CHARGE_DECISION_LABELS[value]}
        placeholder="Not decided yet"
        hint="Told before rather than after, as the policy page says."
      />
      <Button tone="quiet" onClick={submit} disabled={saving}>
        {saving ? 'Recording' : 'Record the decision'}
      </Button>
    </div>
  );
}
